/**
 * SEO per route. Overgenomen van de huidige beautybewust.nl waar het zoekwaarde
 * heeft (lokaal "schoonheidssalon Amsterdam Oost", "duurzame / vegan skincare",
 * "hormonale disbalans", merknamen, behandelnamen) en aangescherpt: kortere
 * titels met het zoekwoord vooraan en beschrijvingen die tot klikken aanzetten.
 *
 * Alle feiten (ervaring, ruiltermijn, telefoon, merken) komen uit de centrale
 * databronnen, zodat SEO nooit iets anders belooft dan de pagina zelf.
 * Bij de koppeling met WordPress kunnen deze waarden 1-op-1 naar Yoast/RankMath.
 */
import { policy, policyCopy } from './policies'
import { founder, salon } from './content'
import { listNl } from '../core/format/text'

export type SeoEntry = {
  title: string
  description: string
  keywords: string[]
}

export const SITE_URL = 'https://beautybewust.nl'
export const SITE_NAME = 'Beauty Bewust'
export const OG_IMAGE =
  'https://cdn.magicpatterns.com/uploads/azzLeCawxKA2qtysfHbXME/18113845093809152-s-80-400.jpg'

/**
 * Delen van de site die niet in Google horen: afrekenen, Mijn omgeving en het beheer.
 * Staat ook als Disallow in robots.txt (utils/seoFiles.ts) — één lijst voor beide.
 */
export const noIndexPrefixes = ['/afrekenen', '/bestelling', '/account', '/nieuwsbrief', '/be']

export const isNoIndexPath = (pathname: string) =>
  noIndexPrefixes.some((p) => pathname === p || pathname.startsWith(`${p}/`))

/**
 * Tabtitel voor de afgeschermde shoppagina's (per prefix uit `noIndexPrefixes`). Zonder deze
 * titel vielen ze in resolveSeo() op "Pagina niet gevonden". Het beheer (/be) zet zijn eigen titel.
 */
export const privatePageTitles: Record<string, string> = {
  '/afrekenen': 'Afrekenen',
  '/bestelling': 'Je bestelling',
  '/account': 'Mijn omgeving',
  '/nieuwsbrief': 'Nieuwsbrief bevestigen',
}

/** De vaste, indexeerbare pagina's (voor de sitemap). Product-, artikel- en juridische pagina's komen uit de data. */
export const staticIndexPaths = ['/', '/webshop', '/routines', '/behandelingen', '/journal', '/over-ons', '/contact']

/** De salonpagina noemt de signature-behandelingen uit de catalogus — hernoemen klopt vanzelf. */
export function treatmentsSeo(signatureNames: string[]): SeoEntry {
  const names = signatureNames.length ? `${listNl(signatureNames)}. ` : ''
  return {
    title: `Gezichtsbehandeling ${salon.city} — bindweefselmassage | ${SITE_NAME}`,
    description: `${names}Resultaatgerichte gezichtsbehandelingen met bindweefselmassage, voor huidverbetering én ontspanning.`,
    keywords: [
      `gezichtsbehandeling ${salon.locality}`,
      'bindweefselmassage gezicht',
      ...signatureNames.slice(0, 2),
      `anti aging behandeling ${salon.locality}`,
      'natuurlijke facelift',
      `schoonheidsspecialist ${salon.city.replace('-', ' ')}`,
    ],
  }
}

/** De webshop noemt de merken uit de catalogus — een nieuw merk staat er vanzelf in. */
export function shopSeo(brandNames: string[]): SeoEntry {
  return {
    title: `Vegan skincare webshop — ${brandNames.join(' & ')} | ${SITE_NAME}`,
    description: `Shop vegan, bijna medicinale skincare van ${listNl(brandNames)}. Gefilterd op huidtype, met gratis advies en ${policyCopy.returnsShort}.`,
    keywords: [
      ...brandNames.map((b) => `${b} kopen`),
      'vegan skincare webshop',
      'vitamine C serum',
      'retinal serum',
      'skincare hormonale huid',
      'rosacea producten',
    ],
  }
}

const buildSeoByPath = (): Record<string, SeoEntry> => ({
  '/': {
    title: `Duurzame schoonheidssalon ${salon.city} & vegan skincare | ${SITE_NAME}`,
    description: `Holistische gezichtsbehandelingen en 100% vegan skincare van emerginC. ${policyCopy.experience} ervaring met hormonale huid, pigment en anti-aging. Persoonlijk advies van ${founder.firstName} via WhatsApp.`,
    keywords: [
      'schoonheidssalon Amsterdam Oost',
      'duurzame schoonheidssalon',
      'vegan skincare',
      'emerginC',
      'hormonale huid',
      'huidadvies',
    ],
  },
  '/routines': {
    title: `Skincare protocollen op maat — pigment, acne & anti-aging | ${SITE_NAME}`,
    description:
      'Complete huidroutines zoals een huidspecialist ze opstelt: in de juiste volgorde, met een persoonlijk huidplan. Voor pigmentvlekken, hormonale onzuiverheden en anti-aging.',
    keywords: [
      'skincare routine',
      'huidroutine op maat',
      'pigmentvlekken verminderen',
      'hormonale acne routine',
      'anti aging routine',
    ],
  },
  // '/behandelingen' → treatmentsSeo(): noemt de signature-behandelingen uit de catalogus.
  '/journal': {
    title: `Skincare blog & podcast — huidtips en bewust leven | ${SITE_NAME}`,
    description: `Tips en bevindingen over huidverzorging van huidspecialist ${founder.name}: de juiste skincare volgorde, droge huid in de winter, SPF, en de ${SITE_NAME} podcast.`,
    keywords: ['skincare blog', 'skincare volgorde', 'droge huid winter', 'beauty podcast', 'bewust leven'],
  },
  '/over-ons': {
    title: `Over ${founder.firstName}, huidspecialist in Amsterdam | ${SITE_NAME}`,
    description: `Maak kennis met ${founder.name}, ${policyCopy.experience} huidspecialist. Eén behandelkamer aan de ${salon.street} in ${salon.locality}, op afspraak.`,
    keywords: [founder.name, 'schoonheidsspecialist Amsterdam', 'huidspecialist Amsterdam'],
  },
  '/contact': {
    title: `Contact & openingstijden | ${SITE_NAME} ${salon.city}`,
    description: `App ${founder.firstName} via WhatsApp (${salon.phone}) of mail ${salon.email}. Persoonlijk antwoord binnen ${policy.responseHours} uur. Salon aan de ${salon.street}, ${salon.locality}, op afspraak.`,
    keywords: [`${SITE_NAME} contact`, `schoonheidssalon ${salon.city.replace('-', ' ')}`, 'huidadvies WhatsApp', `${SITE_NAME} openingstijden`],
  },
})

/** SEO per pad. Bijgewerkt via `rebuildSeo()` zodra de instellingen binnen zijn. */
export const seoByPath: Record<string, SeoEntry> = {}

export function rebuildSeo() {
  Object.keys(seoByPath).forEach((k) => delete seoByPath[k])
  Object.assign(seoByPath, buildSeoByPath())
}

rebuildSeo()
